import { Typography } from "@repo/design-system";
import { COLUMN_DESCRIPTIONS } from "@/lib/columnDescriptions";

interface IColumnEntry {
  name: string;
  description: React.ReactNode;
}

interface IColumnSection {
  title: string;
  entries: IColumnEntry[];
}

const d = COLUMN_DESCRIPTIONS;

const COLUMN_SECTIONS: IColumnSection[] = [
  {
    title: "Basic Information",
    entries: [
      { name: "No.", description: d["No."] },
      { name: "BSA-DB ID", description: d["BSA-DB ID"] },
      { name: "Molecule", description: d["Molecule"] },
    ],
  },
  {
    title: "Biological Activity",
    entries: [
      { name: "Source IDs", description: d["Source IDs"] },
      { name: "Viruses", description: d["Viruses"] },
      {
        name: "Number of Viruses",
        description: d["Number of Viruses"],
      },
      { name: "Standard Type", description: d["Standard Type"] },
      {
        name: "Standard Relation",
        description: d["Standard Relation"],
      },
      { name: "Standard Value", description: d["Standard Value"] },
      { name: "Standard Unit", description: d["Standard Unit"] },
      { name: "Targets", description: d["Targets"] },
      {
        name: "Assay Variability Note",
        description: d["Assay Variability Note"],
      },
    ],
  },
  {
    title: "External Databases",
    entries: [
      { name: "PubChem ID", description: d["PubChem ID"] },
      { name: "ChEMBL ID", description: d["ChEMBL ID"] },
      { name: "BindingDB ID", description: d["BindingDB ID"] },
    ],
  },
  {
    title: "Calculated Properties",
    entries: [
      { name: "Calculated MW", description: d["Calculated MW"] },
      { name: "Calculated logP", description: d["Calculated logP"] },
      { name: "Calculated HBD", description: d["Calculated HBD"] },
      { name: "Calculated HBA", description: d["Calculated HBA"] },
      { name: "Calculated TPSA", description: d["Calculated TPSA"] },
      { name: "Calculated RotB", description: d["Calculated RotB"] },
      {
        name: "QED",
        description: (
          <>
            {d["QED"]}{" "}
            <a
              href="https://doi.org/10.1038/nchem.1243"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-contentBrand underline-offset-2 hover:underline">
              doi:10.1038/nchem.1243
            </a>
          </>
        ),
      },
      {
        name: "SA Score",
        description: (
          <>
            {d["SA Score"]}{" "}
            <a
              href="https://doi.org/10.1186/1758-2946-1-8"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-contentBrand underline-offset-2 hover:underline">
              doi:10.1186/1758-2946-1-8
            </a>
          </>
        ),
      },
      { name: "Cluster ID", description: d["Cluster ID"] },
      { name: "HADES Score", description: d["HADES Score"] },
    ],
  },
];

function ColumnSection({ title, entries }: IColumnSection) {
  return (
    <div className="overflow-hidden rounded-xl border border-borderPrimary">
      <div className="border-b border-borderPrimary bg-backgroundSecondary px-5 py-3">
        <Typography
          variant="paragraph-l-semibold"
          className="text-contentBrand">
          {title}
        </Typography>
      </div>
      {entries.map(({ name, description }, idx) => (
        <div
          key={name}
          className={`grid grid-cols-1 gap-2 px-5 py-3 sm:grid-cols-[200px_1fr] sm:gap-6 ${
            idx < entries.length - 1 ? "border-b border-borderPrimary" : ""
          }`}>
          <Typography variant="paragraph-m-semibold" className="text-b700">
            {name}
          </Typography>
          <Typography
            as="span"
            variant="paragraph-m-regular"
            className="text-contentSecondary">
            {description}
          </Typography>
        </div>
      ))}
    </div>
  );
}

export function Methodology() {
  return (
    <div className="flex flex-col gap-6">
      <Typography as="h2" variant="heading-3" className="text-contentPrimary">
        Molecular Data Table Column Descriptions
      </Typography>
      <div className="flex flex-col gap-4">
        {COLUMN_SECTIONS.map((section) => (
          <ColumnSection key={section.title} {...section} />
        ))}
      </div>
    </div>
  );
}
